import React, { useEffect, useState } from 'react';
import IsoDatePicker from './IsoDatePicker';
import RecurringPicker from './RecurringPicker';
import WaitlistOptIn from './WaitlistOptIn';
import { cbSessionsLocal } from '../utils/availabilityLocal';
import SlotButton from './SlotButton';

export default function CBInlinePicker({
  offering, onPick, bookingRange, cbSessions, timezone, firstBookableTs, live,
  employeesIndex = {}, defaultDate = '', winStart = '', winEnd = '', initialParty = 2
  , hideAdd = false,
  onDraftChange,
}) {
  const [date, setDate] = useState(defaultDate || '');
  const [party, setParty] = useState(Number(initialParty || 2));
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includeDates, setIncludeDates] = useState([]);
  const [instructorFilter, setInstructorFilter] = useState('');
  const addHHMM = (hhmm, minutes) => {
    const [h, m] = hhmm.split(':').map(Number);
    let total = h * 60 + m + Number(minutes || 0);
    let dayCarry = 0;
    while (total >= 1440) { total -= 1440; dayCarry++; }
    const hh = String(Math.floor(total / 60)).padStart(2, '0');
    const mm = String(total % 60).padStart(2, '0');
    return { hhmm: `${hh}:${mm}`, dayCarry };
  };
  const [recurring, setRecurring] = useState({ enabled: !!offering.allow_recurring, count: 4, auto_renew: false });
  const [waitlist, setWaitlist] = useState({ earlier: false, specific_times: [], date: defaultDate || '' });
  const [adding, setAdding] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  async function findSessions() {
    if (!date) return;
    setLoading(true);
    try {
      const args = { dateISO: date, classId: offering.id, capacity: offering.capacity, cbSessions, tz: timezone, firstBookableISO: firstBookableTs, live };
      const r = cbSessionsLocal(args);
      const filtered = (r.sessions || []).filter(s => (!winStart && !winEnd) || (s.start_time >= (winStart || '00:00') && s.start_time <= (winEnd || '23:59')));
      setSessions(filtered);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (date) findSessions();
  }, [date, offering.id, cbSessions, timezone, winStart, winEnd, firstBookableTs, live]);

  // NEW: react to live/schedule/filters changes by refreshing current date automatically
  useEffect(() => {
    if (!date) return;
    findSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(live),
    offering.id,
    offering.duration_min,
    party,
    winStart,
    winEnd,
    timezone,
    firstBookableTs,
    JSON.stringify(cbSessions),
  ]);
  useEffect(() => {
    if (date && !waitlist.date) setWaitlist(w => ({ ...w, date }));
  }, [date]);

  const patternTimes = React.useMemo(() => {
    if (!date) return [];
    const dow = ((new Date(date).getDay() + 6) % 7) + 1;
    return (cbSessions || [])
      .filter(s => s.class_id === offering.id && Number(s.day_of_week) === dow)
      .map(s => s.start_time)
      .sort();
  }, [date, cbSessions, offering.id]);

  const unavailableTimes = React.useMemo(() => {
    const availableSet = new Set((sessions || []).map(s => s.start_time));
    return patternTimes.filter(t => !availableSet.has(t));
  }, [patternTimes, sessions]);

  useEffect(() => {
    if (!bookingRange?.start_date || !bookingRange?.end_date) { setIncludeDates([]); return; }
    const start = new Date(bookingRange.start_date);
    const end = new Date(bookingRange.end_date);
    const out = [];
    const need = Math.max(1, Number(party || 1));

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = d.toISOString().slice(0, 10);
      const args = { dateISO: iso, classId: offering.id, capacity: offering.capacity, cbSessions, tz: timezone, firstBookableISO: firstBookableTs, live };
      const r = cbSessionsLocal(args);
      if ((r.sessions || []).some(s => s.remaining >= need && (!winStart && !winEnd || (s.start_time >= (winStart || '00:00') && s.start_time <= (winEnd || '23:59'))))) out.push(iso);
    }
    setIncludeDates(out);
  }, [bookingRange?.start_date, bookingRange?.end_date, offering.id, offering.capacity, cbSessions, timezone, firstBookableTs, live, party, winStart, winEnd]);

  const instructorOptions = React.useMemo(() => {
    if (!offering.show_instructor_to_clients) return [];
    const ids = new Set((sessions || []).map(s => s.instructor_employee_id).filter(Boolean));
    return Array.from(ids).map(id => ({ id, name: employeesIndex[id]?.name || 'Instructor' })).sort((a, b) => a.name.localeCompare(b.name));
  }, [sessions, offering.show_instructor_to_clients, employeesIndex]);

  const visibleSessions = React.useMemo(() => {
    const need = Math.max(1, Number(party || 1));
    return (sessions || [])
      .filter(s => s.remaining >= need)
      .filter(s => (!winStart && !winEnd) || (s.start_time >= (winStart || '00:00') && s.start_time <= (winEnd || '23:59')))
      .filter(s => !instructorFilter || s.instructor_employee_id === instructorFilter);
  }, [sessions, instructorFilter, party]);

  useEffect(() => {
    if (!date) return;
    if (!includeDates?.length || !includeDates.includes(date)) {
      setDate('');
      setSessions([]);
    }
  }, [date, includeDates?.join('|')]);

  // Emit a draft selection when a session is selected (mirrors Add enablement)
  useEffect(() => {
    if (typeof onDraftChange !== 'function') return;
    const s = (sessions || []).find(x => x.session_id === selectedSession);
    if (!date || !s) { try { onDraftChange(offering, null); } catch (e) {} return; }
    // Compute end time similarly to Add handler
    const addHHMM = (hhmm, minutes) => {
      const [h, m] = hhmm.split(':').map(Number);
      let total = h * 60 + m + Number(minutes || 0);
      let dayCarry = 0;
      while (total >= 1440) { total -= 1440; dayCarry++; }
      const hh = String(Math.floor(total / 60)).padStart(2, '0');
      const mm = String(total % 60).padStart(2, '0');
      return { hhmm: `${hh}:${mm}`, dayCarry };
    };
    const { hhmm: endHHMM, dayCarry } = addHHMM(s.start_time, offering.duration_min);
    const endDate = dayCarry
      ? new Date(new Date(date).setDate(new Date(date).getDate() + dayCarry)).toISOString().slice(0, 10)
      : date;
    const draft = {
      slot_start: `${date}T${s.start_time}:00`,
      slot_end: `${endDate}T${endHHMM}:00`,
      class_session_id: s.session_id,
      class_session_date: date,
      party: Math.max(1, Number(party || 1)),
    };
    try { onDraftChange(offering, draft); } catch (e) {}
  }, [onDraftChange, offering, selectedSession, sessions, date, party, offering?.duration_min]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-600">Date</label>
          <IsoDatePicker
            value={date}
            onChange={setDate}
            minDate={bookingRange?.start_date}
            maxDate={bookingRange?.end_date}
            includeDates={includeDates}
            includeDatesStrict
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600">Party</label>
          <input
            type="number" min={1}
            value={party}
            onChange={e => setParty(Number(e.target.value || 1))}
            className="border rounded px-2 py-1 w-20"
          />
        </div>
        {offering.show_instructor_to_clients && (
          <div className="ml-auto">
            <label className="block text-xs text-gray-600">Instructor</label>
            <select
              value={instructorFilter}
              onChange={e => setInstructorFilter(e.target.value)}
              className="border rounded px-2 py-1 w-44"
              disabled={!date || loading || !instructorOptions.length}
            >
              <option value="">Any</option>
              {instructorOptions.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {loading && <div className="text-xs text-gray-500">Checking…</div>}

      {!loading && !!visibleSessions.length && (
        <div className="flex flex-wrap gap-2">
          {visibleSessions.map(s => {
            const inst = s.instructor_employee_id ? employeesIndex[s.instructor_employee_id] : null;
            const initials = (inst?.name || 'I')
              .split(' ')
              .map(w => w[0])
              .join('')
              .slice(0, 2)
              .toUpperCase();
            const isSelected = selectedSession === s.session_id;
            return (
              <SlotButton
                key={s.session_id}
                disabled={adding}
                onClick={() => {
                  // select session instead of adding
                  setSelectedSession(isSelected ? null : s.session_id);
                }}
                className={isSelected ? 'border-black bg-gray-100' : ''}
              >
                <div className="flex items-center gap-2">
                  <span>{s.start_time}</span>
                  <span className="text-gray-500">({s.remaining} left)</span>
                  {offering.show_instructor_to_clients && (
                    <span className="inline-flex items-center gap-2 ml-1 pl-2 border-l">
                      {inst?.photo_url ? (
                        <img
                          src={inst.photo_url}
                          alt={inst.name}
                          className="w-5 h-5 rounded-full object-cover"
                        />
                      ) : (
                        <span className="w-5 h-5 rounded-full bg-gray-200 text-[10px] flex items-center justify-center">
                          {initials}
                        </span>
                      )}
                      <span className="text-xs text-gray-700">{inst?.name || 'Instructor'}</span>
                    </span>
                  )}
                </div>
              </SlotButton>
            );
          })}
        </div>
      )}

      {/* Add button for selected session */}
      {selectedSession && !hideAdd && (
        <div className="mt-3">
          <button
            disabled={adding}
            className="px-3 py-2 rounded bg-black text-white text-sm disabled:opacity-40"
            onClick={async () => {
              const s = (sessions || []).find(x => x.session_id === selectedSession);
              if (!s) return;
              setAdding(true);
              try {
                const { hhmm: endHHMM, dayCarry } = addHHMM(s.start_time, offering.duration_min);
                const endDate = dayCarry
                  ? new Date(new Date(date).setDate(new Date(date).getDate() + dayCarry)).toISOString().slice(0, 10)
                  : date;
                await onPick(offering, {
                  slot_start: `${date}T${s.start_time}:00`,
                  slot_end: `${endDate}T${endHHMM}:00`,
                  class_session_id: s.session_id,
                  class_session_date: date,
                  party,
                  guests_json: {
                    recurring: (offering.allow_recurring && recurring.enabled)
                      ? { enabled: true, cadence: 'weekly', count: Number(recurring.count || 2), auto_renew: !!recurring.auto_renew }
                      : null,
                    waitlist: (waitlist.earlier || (waitlist.specific_times?.length > 0))
                      ? {
                        enabled: true,
                        earlier: !!waitlist.earlier,
                        specific_times: (waitlist.specific_times || []).filter(Boolean).slice(0, 3),
                        date: waitlist.date || null
                      }
                      : null,
                    date_scope: waitlist.date || date
                  }
                });
                // after adding, clear selection
                setSelectedSession(null);
              } finally { setAdding(false); }
            }}
          >Add</button>
        </div>
      )}

      {!loading && date && !visibleSessions.length && (
        <div className="text-xs text-amber-700">No sessions on this date.</div>
      )}
      <div className="mt-3 grid gap-3">
        {!!offering.allow_recurring && (
          <RecurringPicker value={recurring} onChange={setRecurring} />
        )}
        <WaitlistOptIn
          value={waitlist}
          onChange={setWaitlist}
          unavailableTimes={unavailableTimes}
          minDate={bookingRange?.start_date}
          maxDate={bookingRange?.end_date}
          includeDates={includeDates}
        />
      </div>
    </div>
  );
}
